<template>
  <div class="timer-container">
    <svg class="timer-svg" viewBox="0 0 200 200">
      <!-- Dotted background ring -->
      <circle
        cx="100"
        cy="100"
        r="90"
        fill="none"
        stroke="#0a66c2"
        stroke-width="3"
        stroke-dasharray="1 11"
        stroke-linecap="round"
      />

      <!-- Progress arc -->
      <circle
        ref="progress"
        cx="100"
        cy="100"
        r="90"
        fill="none"
        :stroke="strokeColor"
        stroke-width="3"
        stroke-linecap="round"
        transform="rotate(-90 100 100)"
      />

      <!-- Moving dot -->
      <circle ref="dot" r="5" :fill="strokeColor" />
    </svg>

    <div class="timer-text" :style="{ color: strokeColor }">
      <div class="time">{{ timeText }}</div>
      <div class="label">to approve</div>
    </div>
  </div>
</template>

<script>
import approvalDeadlinesStore from '@/utils/localStorageApprovalDeadlines'

export default {
  name: 'ApprovalTimer',

  props: {
    duration: {
      type: Number,
      default: 60 * 1000, // Milliseconds
    },
    approvalId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      startTime: null,
      timeText: '',
      strokeColor: '#0069b1',
      radius: 90,
      circumference: 0,
      hasTimedOut: false,
      isDestroyed: false,
      frameId: null,
      boundAnimate: null,
    }
  },
  mounted() {
    this.circumference = 2 * Math.PI * this.radius

    const circle = this.$refs.progress
    circle.style.strokeDasharray = this.circumference
    circle.style.strokeDashoffset = this.circumference

    this.$refs.dot.setAttribute('cx', 100)
    this.$refs.dot.setAttribute('cy', 10)

    const savedDeadline = approvalDeadlinesStore.get(this.approvalId)

    const now = Date.now()

    if (savedDeadline) {
      const remaining = savedDeadline - now

      if (remaining <= 0) {
        // already expired on refresh
        this.hasTimedOut = true

        approvalDeadlinesStore.remove(this.approvalId)
        this.$emit('timeout')
        return
      }

      // Reconstruct animation start time
      this.startTime = performance.now() - (this.duration - remaining)
    } else {
      // First load
      const deadline = now + this.duration
      approvalDeadlinesStore.set(this.approvalId, deadline)
    }

    const initialTime = savedDeadline
      ? Math.max(savedDeadline - now, 0)
      : this.duration
    this.timeText = this.getTimeText(initialTime)

    this.boundAnimate = this.animate.bind(this)
    this.frameId = requestAnimationFrame(this.boundAnimate)
  },
  methods: {
    animate(now) {
      if (this.isDestroyed || !this.$refs.progress || !this.$refs.dot) return

      this.frameId = requestAnimationFrame(this.boundAnimate)

      if (!this.startTime) this.startTime = now

      const elapsed = now - this.startTime
      const remaining = Math.max(this.duration - elapsed, 0)

      const rawProgress = Math.max(remaining / this.duration, 0.00001)

      const eased = Math.pow(rawProgress, 0.9)

      // Color change near end
      if (rawProgress < 0.333) {
        this.strokeColor = '#FB6D00'
      } else {
        this.strokeColor = '#0069b1'
      }

      // Counter‑clockwise arc
      const offset = this.circumference * eased
      this.$refs.progress.style.strokeDashoffset = offset

      // Time text
      this.timeText = this.getTimeText(remaining)

      // Dot position
      const angle = -2 * Math.PI * eased - Math.PI / 2
      const x = 100 + this.radius * Math.cos(angle)
      const y = 100 + this.radius * Math.sin(angle)

      this.$refs.dot.setAttribute('cx', x)
      this.$refs.dot.setAttribute('cy', y)

      if (remaining <= 0) {
        if (!this.hasTimedOut) {
          this.hasTimedOut = true
          approvalDeadlinesStore.remove(this.approvalId)
          this.$emit('timeout')
        }
        cancelAnimationFrame(this.frameId)
        this.frameId = null
        return
      }
    },
    getTimeText(duration) {
      const totalSeconds = Math.ceil(duration / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },
  },
  beforeDestroy() {
    this.isDestroyed = true
    if (this.frameId) {
      cancelAnimationFrame(this.frameId)
    }
  },
}
</script>

<style scoped>
.timer-container {
  position: relative;
  height: 190px;
  width: 190px;
  margin: 0 auto;
}

.timer-svg {
  width: 100%;
  height: 100%;
}

.timer-text {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: color 300ms;
}

.time {
  font-size: 40px;
  font-weight: bolder;
}

.label {
  font-size: 10px;
}

circle {
  transition: stroke 300ms, fill 300ms;
}
</style>
